import { Trans } from 'react-i18next';
import { CountryProps, Option } from '../dropDownInput/dropDownInput.type';
import { GB, IN, NZ, US } from 'country-flag-icons/string/3x2';
import React, { ChangeEvent, ReactNode } from 'react';
import { PinOptionType, UserOperation } from './type';
import { isArray, isString, map, startCase } from 'lodash';
import DropDownInput from '../dropDownInput/dropDownInput';
import { UserType } from '@inplay/models';
import Input from '../Input/input';
import { FieldConfig } from '../../strategies/userPresentation/userPresentationStrategy.interface';
import RadioGroup from '../radioGroup';
import { getMaskedPassword } from '../../utils/helper';
import ShowEye from '../../assets/show_eye.png';
import CloseEye from '../../assets/close_eye.png';
import DesktopPinInput from '../desktopPinInput/desktopPinInput';
import FileUpload from '../fileUpload/fileUpload';

/**
 * Renders a labeled form row with a consistent layout.
 *
 * @param label - The label text displayed on the left side of the row.
 * @param children - The form element (e.g., input, dropdown, date picker) displayed on the right side.
 * @returns A JSX element representing a horizontal form row with label and input.
 *
 * Layout: 30% width for the label, 70% width for the input.
 * Adds top margin except for the first row.
 */
export const renderFormRow = (label: ReactNode, children: ReactNode) => (
  <div className="flex mt-4 items-start">
    <div className="w-[30%] pt-2 text-sm text-[#1A1A1A] leading-[20px] min-h-[40px]">
      {label}
    </div>
    <div className="w-[70%] min-h-[40px]">{children}</div>
  </div>
);

export type UserFormValues = {
  userType: string;
  firstName: string;
  lastName: string;
  venue: string;
  email: string;
  mobile: string;
  venueBranch: string[];
  country: CountryProps;
};

const displayNames: Intl.DisplayNames = new Intl.DisplayNames(['en'], {
  type: 'region',
});

export const countries: CountryProps[] = [
  { code: 'NZ', dialCode: '+64', flag: NZ },
  { code: 'GB', dialCode: '+44', flag: GB },
  { code: 'US', dialCode: '+1', flag: US },
  { code: 'IN', dialCode: '+91', flag: IN },
].map((country) => ({
  ...country,
  name: displayNames.of(country.code),
}));

/**
 * Renders a two-column layout row for displaying a label and its corresponding value
 * in a user detail view.
 *
 * @param label - The left-hand label (e.g., "Email", "Role") shown in bold.
 * @param children - The right-hand content or value related to the label.
 *
 * @returns A styled flex container with label and value aligned horizontally.
 */
export const renderViewUserRow = (
  label: string,
  value: string,
): JSX.Element => (
  <div className="flex mt-4 items-start">
    <div className="w-[30%] font-semibold  text-sm  text-[#1A1A1A] min-h-[20px]">
      <Trans i18nKey={label} />
    </div>
    <div className="w-[70%] text-sm text-[#1F1F1F] min-h-[20px]">{value}</div>
  </div>
);

/**
 * Renders a user detail row specifically for displaying a profile photo.
 *
 * This component is used when the user detail key is "Photo".
 * It displays the label on the left and the user's profile picture on the right.
 *
 * Props:
 * @param {string} label - The label for the row (typically "Photo").
 * @param {string} value - The image URL for the profile photo.
 */
export const renderViewUserProfileRow = (
  label: string,
  value: string,
): JSX.Element => (
  <div className="flex mt-4 items-start">
    <div className="w-[30%] font-semibold  text-sm  text-[#1A1A1A] min-h-[20px]">
      <Trans i18nKey={label} />
    </div>
    <div className="w-[70%]">
      <img
        src={value}
        alt={'User profile'}
        className="w-32 h-32 rounded-full object-cover"
      />
    </div>
  </div>
);

export const userTypeOptions: Option[] = [
  {
    label: startCase(UserType.ORGANIZATION_ADMIN),
    value: UserType.ORGANIZATION_ADMIN,
  },
  {
    label: startCase(UserType.SUB_ORGANIZATION_ADMIN),
    value: UserType.SUB_ORGANIZATION_ADMIN,
  },
  {
    label: startCase(UserType.VENUE_ADMIN),
    value: UserType.VENUE_ADMIN,
  },
  {
    label: startCase(UserType.VENUE_MANAGER),
    value: UserType.VENUE_MANAGER,
  },
  {
    label: startCase(UserType.VENUE_STAFF),
    value: UserType.VENUE_STAFF,
  },
  {
    label: startCase(UserType.VENUE_BRANCH_LOGIN),
    value: UserType.VENUE_BRANCH_LOGIN,
  },
];

export const renderModalTitle = (operation: UserOperation): JSX.Element => {
  if (operation === UserOperation.ADD) {
    return <Trans i18nKey="userManagement.addUser" />;
  } else if (operation === UserOperation.VIEW) {
    return <Trans i18nKey="userManagement.userDetail" />;
  } else {
    return <Trans i18nKey="userManagement.editUser" />;
  }
};

/**
 *
 * @param userDetails
 * @returns the user details section
 * @description This method will be used to return the user details section wherre the logged in user can view the user details
 */
export const renderViewUser = (
  userDetails: Record<string, string>,
): JSX.Element => {
  console.log('User detail of data', userDetails);
  return (
    <>
      {map(userDetails, (value: string, key: string) => {
        return key === 'Photo' && value
          ? renderViewUserProfileRow(key, value)
          : renderViewUserRow(key, value);
      })}
    </>
  );
};

/**
 * Renders a form field based on the provided configuration
 *
 * @param config - The field configuration
 * @param value - The current value of the field
 * @param onChange - Function to call when the value changes
 * @param errorMessage - Optional error message to display
 * @returns A JSX element representing the form field
 */

export const renderFormField = (
  config: FieldConfig,
  value: any,
  onChange: (value: any) => void,
  errorMessage?: string,
  uploadLabel?: string,
  supportedText?: string,
  handleFileSelect?: ((file: File) => void) | undefined,
  handleFileDeselect?: () => void,
) => {
  const renderField = (): JSX.Element => {
    switch (config.type) {
      case 'text':
      case 'email':
        return (
          <Input
            placeholder={config.placeholder}
            value={value || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange(event.target.value)
            }
            errorMessage={errorMessage}
            disabled={config.disabled}
          />
        );

      case 'dropdown':
        return (
          <DropDownInput
            placeholder={config.placeholder}
            options={config.options || []}
            selected={value}
            onSelect={(value: string | string[]) => {
              if (isString(value)) {
                onChange(value);
              }
            }}
            errorMessage={errorMessage}
            disabled={config.disabled}
          />
        );

      case 'multiselect':
        return (
          <DropDownInput
            placeholder={config.placeholder}
            isMulti
            options={config.options || []}
            selected={value || []}
            onSelect={(value: string | string[]) => {
              if (isArray(value)) {
                onChange(value);
              }
            }}
            errorMessage={errorMessage}
            disabled={config.disabled}
          />
        );

      case 'phone':
        return (
          <div className="flex w-full gap-2">
            <div className="w-[30%]">
              <DropDownInput
                isCountry
                countries={countries}
                selectedCountry={value?.country || countries[0]}
                onSelectCountry={(country: CountryProps) => {
                  onChange({ ...value, country });
                }}
              />
            </div>
            <div className="w-[70%]">
              <Input
                value={value?.number || ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  onChange({ ...value, number: event.target.value });
                }}
                placeholder={config.placeholder}
                errorMessage={errorMessage}
                disabled={config.disabled}
              />
            </div>
          </div>
        );

      case 'radio':
        return (
          <div>
            {config.options && (
              <RadioGroup
                options={config.options.map((option: Option) => ({
                  label: option.label,
                  value: option.value as string,
                }))}
                name={config.id}
                value={value || ''}
                onChange={onChange}
                errorMessage={errorMessage}
                horizontal={true}
              />
            )}
            {config.description && (
              <p className="text-gray-500 text-xs mt-1">{config.description}</p>
            )}
          </div>
        );
      case 'photo':
        return (
          <div>
            <FileUpload
              previewImage
              showEditProfile
              onFileSelect={handleFileSelect}
              onDeselect={handleFileDeselect}
              previewUrl={
                value?.profilePictureUrl || value?.profilePictureBase64 || ''
              }
              selectedFileName={value?.fileName || ''}
              buttonLabel={uploadLabel}
              supportedFormatsText={supportedText}
              acceptedTypes="image/jpeg, image/png"
              maxSize={2}
            />
          </div>
        );
      case 'pin':
        return (
          <div className="flex flex-col gap-3">
            {/* Radio buttons for PIN options */}
            {config.options && (
              <RadioGroup
                options={config.options.map((opt: Option) => ({
                  label: opt.label,
                  value: opt.value as string,
                }))}
                name={config.id}
                value={value?.option || ''}
                onChange={(option) => {
                  // When changing the option, update both the option and the PIN value
                  if (option === 'default') {
                    // For auto-generate, generate a random 4-digit PIN
                    const randomPin: string = Math.floor(
                      1000 + Math.random() * 9000,
                    ).toString();
                    onChange({ option, pin: randomPin });
                  } else {
                    // For custom, keep the existing PIN or set to empty
                    onChange({ option, pin: '' });
                  }
                }}
                horizontal={true}
              />
            )}

            {/* Only show PIN input when an option is selected */}
            {value?.option && (
              <div className="justify-between flex">
                <DesktopPinInput
                  otp={value?.pin || ''}
                  setOtp={(pin) => onChange({ ...value, pin })}
                  inputLength={4}
                  placeholder={config.placeholder}
                  hasError={!!errorMessage}
                  isDisable={value?.option !== PinOptionType.CUSTOM}
                  inputClassName="bg-[#F7F7F7] rounded-[18px] text-sm w-12 h-12 text-center"
                />

                {config.description && (
                  <p className="text-gray-500 text-xs mt-1">
                    {config.description}
                  </p>
                )}
                {value?.option === PinOptionType.DEFAULT && (
                  <div className="w-[40%] ml-5">
                    <button
                      type="button"
                      onClick={
                        value?.option === PinOptionType.DEFAULT
                          ? () => {
                              const randomPin: string = Math.floor(
                                1000 + Math.random() * 9000,
                              ).toString();
                              onChange({ ...value, pin: randomPin });
                            }
                          : undefined
                      }
                      className="h-10 w-full rounded-full px-3 py-2 bg-transparent border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center whitespace-nowrap"
                    >
                      <Trans i18nKey={'userManagement.generatePin'} />
                    </button>
                  </div>
                )}
              </div>
            )}
            {(!!errorMessage || value?.pin?.length !== 4) && (
              <p
                className="text-[#B60000] text-sm
               mt-1"
              >
                {errorMessage}
              </p>
            )}
          </div>
        );
      case 'readonly':
        let displayValue: string = config.defaultValue || '';

        // If readonly field is a dropdown or multiselect, get the label from options
        if (config.options) {
          // if value is an array, the field was multiselect, so we must get all labels
          if (Array.isArray(value)) {
            displayValue = config.options
              .filter((opt: Option) => value.includes(opt.value))
              .map((opt: Option) => opt.label)
              .join(', ');
          } else if (typeof value === 'string') {
            displayValue =
              config.options.find((opt: Option) => opt.value === value)
                ?.label || '';
          }
        }
        return (
          <Input
            value={displayValue}
            disabled={true}
            onChange={() => {}} // Empty function since it's readonly
          />
        );
      default:
        const error: string = `Unsupported field type: ${config.type}`;
        console.error(error);
        throw new Error(error);
    }
  };

  return renderFormRow(<Trans i18nKey={config.label} />, renderField());
};

/**
 *
 * @param errorMessage
 * @param setErrorMessage
 * @param userDetails
 * @param handleChange
 * @param setFieldError
 * @returns add user form for that the logged in user can use to create a user
 */
export const renderAddUser = (
  errorMessage: Record<string, string>,
  setErrorMessage: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  userDetails: UserFormValues,
  handleChange: (field: keyof UserFormValues, value: any) => void,
  setFieldError: (field: string, error: string) => void,
) => {
  return (
    <>
      {renderFormRow(
        <Trans i18nKey="userManagement.userTypeTitle" />,
        <>
          <DropDownInput
            placeholder="Select"
            options={userTypeOptions}
            errorMessage={errorMessage.userType}
            selected={userDetails.userType}
            onSelect={(value: string | string[]) => {
              if (typeof value === 'string') {
                handleChange('userType', value);
                if (errorMessage.userType) {
                  setErrorMessage((prev) => ({ ...prev, userType: '' }));
                }
              }
            }}
          />
        </>,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.firstName" />,
        <>
          <Input
            placeholder="Enter first name"
            value={userDetails.firstName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value: string = event.target.value;
              handleChange('firstName', value);
              setFieldError('firstName', value ? '' : 'First name is required'); //TODO: Khushbu to implement tranlation for the error message
            }}
            errorMessage={errorMessage.firstName}
          />
        </>,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.lastName" />,
        <>
          <Input
            placeholder="Enter last name"
            value={userDetails.lastName}
            errorMessage={errorMessage.lastName}
            onChange={(e) => {
              const val = e.target.value;
              handleChange('lastName', val);
              setFieldError('lastName', val ? '' : 'Last name is required'); //TODO: Khushbu to implement tranlation for the error message
            }}
          />
        </>,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.venue" />,
        <Input
          value={userDetails.venue}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleChange('venue', event.target.value)
          }
          disabled={true}
        />,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.venueBranch" />,
        <>
          <DropDownInput
            placeholder="Select"
            isMulti
            options={[]}
            selected={userDetails.venueBranch}
            onSelect={(value: string | string[]) => {
              if (Array.isArray(value)) {
                handleChange('venueBranch', value);
                if (errorMessage.venueBranch && value.length > 0) {
                  setErrorMessage((previous) => ({
                    ...previous,
                    venueBranch: '',
                  }));
                }
              }
            }}
            errorMessage={errorMessage.venueBranch}
          />
        </>,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.email" />,
        <>
          <Input
            value={userDetails.email}
            onChange={(e) => {
              const val = e.target.value;
              handleChange('email', val);
              const valid = val.includes('@');
              setFieldError('email', valid ? '' : 'Invalid email address'); //TODO: Khushbu to implement translation for the error message
            }}
            placeholder="Enter Email address" //TODO: Khushbu to implement translation for the placeholder
            errorMessage={errorMessage.email}
          />
        </>,
      )}

      {renderFormRow(
        <Trans i18nKey="userManagement.mobile" />,
        <div className="flex w-full gap-2">
          <div className="w-[30%]">
            <DropDownInput
              isCountry
              countries={countries}
              selectedCountry={userDetails.country}
              onSelectCountry={(country) => {
                handleChange('country', country);
              }}
            />
          </div>
          <div className="w-[70%]">
            <Input
              value={userDetails.mobile}
              onChange={(e) => {
                const val = e.target.value;
                handleChange('mobile', val);
                setFieldError('mobile', val ? '' : 'Mobile number is required'); //TODO: Khushbu to implement translation for the error message
              }}
              placeholder="Enter mobile number" //TODO: Khushbu to implement translation for the placeholder
              errorMessage={errorMessage.mobile}
            />
          </div>
        </div>,
      )}
    </>
  );
};

export const renderCreationForm = (
  visibleFields: FieldConfig[],
  formValues: Record<string, any>,
  handleChange: (field: string, value: any) => void,
  errorMessage: Record<string, string>,
  uploadLabel?: string,
  supportedText?: string,
  handleFileSelect?: (file: File) => void,
  handleDeselect?: () => void,
) => {
  console.log('Rendering form with values:', visibleFields);
  console.log(
    'Visible fields:',
    visibleFields.map((f) => `${f.id}: ${f.defaultValue}`),
  );

  return (
    <div>
      {visibleFields.map((config) => (
        <div key={config.id}>
          {renderFormField(
            config,
            formValues[config.id],
            (value) => handleChange(config.id, value),
            errorMessage[config.id],
            uploadLabel,
            supportedText,
            handleFileSelect,
            handleDeselect,
          )}
        </div>
      ))}
    </div>
  );
};

export const initializeFormValues = (fields: FieldConfig[]) => {
  const initialValues: Record<string, any> = {};
  fields.forEach((f) => {
    initialValues[f.id] = f.defaultValue ?? '';
  });
  return initialValues;
};

export const renderReviewFields = (
  fields: FieldConfig[],
  formValues: Record<string, any>,
  isUniversalStaffLoginReview?: boolean,
  showPassword?: boolean,
  setShowPassword?: (showPassword: boolean) => void,
  password?: string,
) => {
  if (fields.length === 0) return;

  return (
    <>
      {fields.map((fieldConfig: FieldConfig) => {
        return (
          <div className="flex gap-[50px] items-center text-[12px] text-[#1A1A1A] mt-4">
            <div className="font-semibold w-[130px]">{fieldConfig.label}</div>
            <span>
              {fieldConfig.type === 'dropdown'
                ? fieldConfig.options?.filter(
                    (option) => option.value === formValues[fieldConfig.id],
                  )[0]?.label
                : formValues[fieldConfig.id]}
            </span>
          </div>
        );
      })}
      {isUniversalStaffLoginReview && (
        <div className="w-full gap-[50px] text-black font-helvetica text-xs font-[500] text-left flex items-center">
          <label className="w-[130px] text-black font-helvetica text-xs font-[600] text-left"></label>
          <div className="flex items-center">
            <span className="text-[#0014FF]">
              {showPassword ? password : getMaskedPassword(password || '')}
            </span>
            <div
              className="ml-2 cursor-pointer"
              onClick={() => setShowPassword && setShowPassword(!showPassword)}
            >
              <img
                src={showPassword ? ShowEye : CloseEye}
                alt={showPassword ? 'Hide Password' : 'Show Password'}
                className="w-5 h-3"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
